/**
 * The support queue and one ticket's thread.
 *
 * **An internal note is visibly a note.** It is rendered differently and labelled, because the
 * one mistake this screen can make is an agent believing they replied to somebody when they wrote
 * to themselves — the SLA clock agrees with the agent, not with the label, so a note that looked
 * like a reply would let a ticket breach while everybody thought it was answered.
 *
 * **The queue is ordered by what is due soonest, from the server.** Newest-first is how the
 * bottom of a queue never gets read, and re-sorting in the browser would sort the page in hand
 * rather than the queue.
 */
import { useState } from 'react';
import { PhoneIntake } from './SupportDesk';
import { fillMacro, macroLabel } from './ReplyMacros';
import { StatusChip, type Tone } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, ScopeAction, Section, UrgencyBanner } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useBookingComms,
  useRecordingLink,
  useReopenComms,
  useSuspendComms,
} from '../lib/api/hooks/comms';
import {
  useAssignTicket,
  useEscalateToDispute,
  useReplyToTicket,
  useSetTicketStatus,
  useSupportMacros,
  useTicket,
  useTickets,
} from '../lib/api/hooks/support';
import { formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 50;
const ALL = 'every state';

/** The server's vocabulary — ops read the same words in the logs. */
const STATES = [
  ALL,
  'open',
  'pending_customer',
  'pending_internal',
  'resolved',
  'closed',
  'reopened',
];

/* Its own word: the priority filter read "every state" (live 2026-10-30). */
const ANY_PRIORITY = 'every priority';
const PRIORITIES = [ANY_PRIORITY, 'urgent', 'high', 'normal', 'low'];

const MOVES = ['pending_customer', 'pending_internal', 'resolved', 'closed'];

/**
 * How a call ended, in the words a support agent would use.
 *
 * The two `rejected_*` outcomes are the masking **working** — somebody dialled a platform
 * number from a phone belonging to neither party, or after the window shut. Labelling them as
 * failures would make an agent chase a fault that is the system doing its job.
 */
export function callOutcome(outcome: string): string {
  if (outcome === 'rejected_unknown_caller') return 'blocked — not one of the two parties';
  if (outcome === 'rejected_window_closed') return 'blocked — the call window had closed';
  return outcome.replace(/_/g, ' ');
}

/** A duration an agent reads rather than converts. */
export function callLength(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return 'no answer';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export function priorityTone(priority: string): Tone {
  if (priority === 'urgent') return 'error';
  if (priority === 'high') return 'warn';
  return 'neutral';
}

export function statusTone(status: string): Tone {
  if (status === 'resolved' || status === 'closed') return 'success';
  if (status === 'reopened') return 'error';
  if (status.startsWith('pending')) return 'warn';
  return 'neutral';
}

/** Overdue is a fact about the clock, not a status somebody set. */
export function isOverdue(
  row: { first_responded_at?: string | null; first_response_due_at: string },
  now = Date.now(),
): boolean {
  // `?? null` because the generated type makes an unset optional field `undefined`, and an
  // unanswered ticket is unanswered either way — the two spellings must not disagree about
  // whether somebody has been replied to.
  return (row.first_responded_at ?? null) === null && Date.parse(row.first_response_due_at) < now;
}

/**
 * What the first-reply clock is doing, in words (admin 21 §2). A ticket waiting on the person
 * under a policy that pauses for it reads "paused", never "overdue": the server says so in
 * `clock_paused`, because only it knows which policy version the ticket was pinned to.
 */
export function clockLabel(
  row: {
    first_responded_at?: string | null;
    first_response_due_at: string;
    clock_paused?: boolean;
  },
  now = Date.now(),
): string {
  if (row.first_responded_at) return 'answered';
  if (row.clock_paused) return 'paused — waiting on them';
  return isOverdue(row, now) ? 'overdue' : 'within the promise';
}

/** A safety ticket carries the urgency banner until it is closed, from the moment it lands. */
export function needsSafetyBanner(row: { category: string; status: string }): boolean {
  return row.category === 'safety' && row.status !== 'resolved' && row.status !== 'closed';
}

/** Who has it, or that nobody does — an unassigned ticket is a queue problem, not a blank. */
/** Where a ticket can go from here. Never where it already is (the server answers 409), and a
 *  closed ticket only reopens: moving it straight back to a working state skipped the reopen
 *  count, which is the number that says a resolution did not hold (live 2026-10-30). */
export function movesFrom(status: string): string[] {
  return status === 'closed' ? ['reopened'] : MOVES.filter((m) => m !== status);
}

export function ticketHolder(adminId: number | null | undefined): string {
  return adminId === null || adminId === undefined ? 'nobody yet' : `admin ${adminId}`;
}

export function Tickets() {
  const { scope, adminId, toast } = useConsole();
  const [state, setState] = useState(ALL);
  const [priority, setPriority] = useState(ANY_PRIORITY);
  const [page, setPage] = useState(1);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [moveTo, setMoveTo] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [suspendingId, setSuspendingId] = useState<number | null>(null);
  const [intake, setIntake] = useState(false);

  const list = useTickets({
    page,
    page_size: PAGE_SIZE,
    ...(state === ALL ? {} : { ticket_status: state }),
    ...(priority === ANY_PRIORITY ? {} : { priority }),
  });
  const detail = useTicket(openCode);
  const moves = movesFrom(detail.data?.ticket.status ?? '');
  const moveTarget = moves.includes(moveTo) ? moveTo : moves[0]!;
  const send = useReplyToTicket();
  const move = useSetTicketStatus();
  const escalate = useEscalateToDispute();
  const take = useAssignTicket();
  const bookingCode = detail.data?.booking_public_code ?? null;
  const comms = useBookingComms(bookingCode);
  const suspend = useSuspendComms();
  const reopen = useReopenComms();
  const recording = useRecordingLink();
  const macros = useSupportMacros();
  const macroRows = macros.data?.items ?? [];

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  if (openCode !== null) {
    return (
      <DetailPage
        title={openCode}
        onClose={() => {
          setOpenCode(null);
          setReply('');
          setInternal(false);
        }}
        actions={
          detail.isSuccess ? (
            <>
              {/* Named, so two agents do not each work it believing the other has not. */}
              {detail.data.ticket.assigned_admin_id !== adminId && (
                <ScopeAction
                  scope={scope}
                  need="support"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    take.mutate(
                      { code: openCode, adminId },
                      {
                        onSuccess: () => toast({ tone: 'success', message: 'Yours' }),
                        onError: fail,
                      },
                    )
                  }
                >
                  Take this ticket
                </ScopeAction>
              )}
              {detail.data.ticket.booking_id !== null && (
                <ScopeAction
                  scope={scope}
                  need="support"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEscalating(true)}
                >
                  Escalate to dispute
                </ScopeAction>
              )}
            </>
          ) : null
        }
      >
        {detail.isPending && <LoadingState message="Reading the ticket…" />}
        {detail.isError && (
          <ErrorState title="Could not read the ticket" cause={errorMessage(detail.error)} />
        )}

        {detail.isSuccess && (
          <>
            {needsSafetyBanner(detail.data.ticket) && (
              <SafetyBanner ticket={detail.data.ticket} />
            )}
            <Section title={detail.data.ticket.subject}>
              <KV
                rows={[
                  ['Category', detail.data.ticket.category.replace(/_/g, ' ')],
                  ['Priority', detail.data.ticket.priority],
                  ['State', detail.data.ticket.status.replace(/_/g, ' ')],
                  ['Channel', detail.data.ticket.channel],
                  // A pandit's deduction query (CSR-137): the payable and the line questioned.
                  ...(detail.data.ticket.payable_id != null
                    ? [
                        ['Payable', `#${detail.data.ticket.payable_id}`] as const,
                        [
                          'Questioned line',
                          detail.data.ticket.deduction_kind ?? 'the whole payable',
                        ] as const,
                      ]
                    : []),
                  [
                    'Held by',
                    ticketHolder(detail.data.ticket.assigned_admin_id),
                  ],
                  ['First reply', clockLabel(detail.data.ticket)],
                  ...(detail.data.ticket.reopened_count > 0
                    ? [
                        [
                          'Reopened',
                          `${detail.data.ticket.reopened_count} time${detail.data.ticket.reopened_count === 1 ? '' : 's'}`,
                        ] as const,
                      ]
                    : []),
                ]}
              />
            </Section>

            <Section title="Thread" note="Internal notes are shown here and never to the user">
              {detail.data.messages.map((m) => (
                <div
                  key={m.id}
                  style={{
                    marginBottom: 'var(--sp-md)',
                    paddingLeft: 'var(--sp-sm)',
                    borderLeft: `3px solid ${
                      m.is_internal_note ? 'var(--c-warn)' : 'var(--c-border)'
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--fs-admin-colhead)',
                      color: 'var(--c-ink2)',
                    }}
                  >
                    {m.author_type} · {formatDateTime(m.created_at)}
                    {/* Labelled, not merely coloured. An agent who believes a note was a reply
                        lets the ticket breach while everybody thinks it was answered. */}
                    {m.is_internal_note ? ' · internal note, the user cannot see this' : ''}
                  </div>
                  <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', whiteSpace: 'pre-wrap' }}>
                    {m.body}
                  </p>
                </div>
              ))}
            </Section>

            <Section title="Reply">
              {macroRows.length > 0 && (
                /* Product doc 13 §5. What the ticket knows is filled in; every other
                   {placeholder} stays visible for the agent to write before sending. */
                <Select
                  label="Start from a macro"
                  placeholder="Choose a macro…"
                  value=""
                  options={macroRows.map(macroLabel)}
                  onChange={(label) => {
                    const row = macroRows.find((r) => macroLabel(r) === label);
                    if (row)
                      setReply(
                        fillMacro(row.body, { ticket_code: openCode, booking_code: bookingCode }),
                      );
                  }}
                />
              )}
              <Input label="Message" value={reply} onChange={setReply} style={{ width: '100%' }} />
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--sp-md)',
                  alignItems: 'center',
                  marginTop: 'var(--sp-sm)',
                }}
              >
                <Toggle
                  label="Internal note — the user will not see this, and it does not answer them"
                  on={internal}
                  onChange={setInternal}
                />
                <ScopeAction
                  scope={scope}
                  need="support"
                  size="sm"
                  disabled={reply.trim() === '' || send.isPending}
                  onClick={() =>
                    send.mutate(
                      { code: openCode, body: reply, internal },
                      {
                        onSuccess: () => {
                          setReply('');
                          toast({
                            tone: 'success',
                            message: internal ? 'Note saved' : 'Reply sent',
                          });
                        },
                        onError: fail,
                      },
                    )
                  }
                >
                  {internal ? 'Save note' : 'Send reply'}
                </ScopeAction>
              </div>
            </Section>

            {bookingCode !== null && (
              <Section
                title="Calls on this booking"
                note="Neither party ever sees the other's number. Every session is shown, not just the live one — a reassignment closes one and opens another, and the window somebody is asking about has usually already ended."
              >
                {comms.isPending && <LoadingState message="Reading the call history…" />}
                {comms.isError && (
                  <ErrorState
                    title="Could not read the call history"
                    cause={errorMessage(comms.error)}
                  />
                )}
                {comms.isSuccess && comms.data.sessions.length === 0 && (
                  <EmptyState
                    inline
                    title="No call line was ever opened"
                    body="The line opens the first time either party taps Call, and only after payment."
                  />
                )}
                {comms.isSuccess &&
                  comms.data.sessions.map((s) => (
                    <div
                      key={s.id}
                      style={{
                        display: 'flex',
                        gap: 'var(--sp-md)',
                        alignItems: 'center',
                        marginBottom: 'var(--sp-sm)',
                      }}
                    >
                      <span style={{ flex: 1, fontSize: 'var(--fs-admin-ui)' }}>
                        {s.virtual_number} · {s.status}
                        {s.closed_reason ? ` · ${s.closed_reason.replace(/_/g, ' ')}` : ''}
                      </span>
                      {s.status === 'active' && (
                        <ScopeAction
                          scope={scope}
                          need="support"
                          size="sm"
                          variant="ghost"
                          onClick={() => setSuspendingId(s.id)}
                        >
                          Suspend
                        </ScopeAction>
                      )}
                      {s.status === 'suspended' && (
                        <ScopeAction
                          scope={scope}
                          need="support"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            reopen.mutate(s.id, {
                              onSuccess: () =>
                                toast({ tone: 'success', message: 'Calling resumed' }),
                              onError: fail,
                            })
                          }
                        >
                          Reopen
                        </ScopeAction>
                      )}
                    </div>
                  ))}

                {comms.isSuccess &&
                  comms.data.calls.map((c) => (
                    <div
                      key={c.id}
                      style={{
                        display: 'flex',
                        gap: 'var(--sp-md)',
                        alignItems: 'center',
                        marginBottom: 'var(--sp-xs)',
                        fontSize: 'var(--fs-admin-ui)',
                      }}
                    >
                      <span style={{ flex: 1 }}>
                        {c.direction.replace(/_/g, ' ')} · {callOutcome(c.outcome)} ·{' '}
                        {callLength(c.duration_seconds)}
                      </span>
                      {c.has_recording && (
                        <ScopeAction
                          scope={scope}
                          need="support"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            recording.mutate(c.id, {
                              onSuccess: (link) =>
                                toast({
                                  tone: 'info',
                                  message: `Link valid for ${link.expires_in_seconds}s · this access is on the audit log`,
                                }),
                              onError: fail,
                            })
                          }
                        >
                          Open the recording
                        </ScopeAction>
                      )}
                    </div>
                  ))}
              </Section>
            )}

            <Section title="Move it" note="Waiting on the customer pauses the clock; waiting on us does not">
              <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end' }}>
                <Select label="To" value={moveTarget} options={moves} onChange={setMoveTo} />
                <ScopeAction
                  scope={scope}
                  need="support"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    move.mutate(
                      { code: openCode, status: moveTarget },
                      {
                        onSuccess: () => toast({ tone: 'success', message: 'Moved' }),
                        onError: fail,
                      },
                    )
                  }
                >
                  Move
                </ScopeAction>
              </div>
            </Section>
          </>
        )}

        {suspendingId !== null && (
          <ConfirmAction
            open
            title="Pause calling on this booking?"
            confirmLabel="Pause it"
            phrase="SUSPEND"
            tone="error"
            reason="Why — both parties are affected, and this is audited"
            consequences={[
              'Neither party can reach the other on the masked number until it is reopened.',
              'Support remains reachable to both of them.',
              'The reason is written to the audit log with your name against it.',
            ]}
            onCancel={() => setSuspendingId(null)}
            onConfirm={(reason) =>
              suspend.mutate(
                { id: suspendingId, reason },
                {
                  onSuccess: () => {
                    setSuspendingId(null);
                    toast({ tone: 'success', message: 'Calling paused' });
                  },
                  onError: (error) => {
                    setSuspendingId(null);
                    fail(error);
                  },
                },
              )
            }
          />
        )}

        {escalating && detail.isSuccess && (
          <ConfirmAction
            open
            title="Escalate this to a formal dispute?"
            confirmLabel="Escalate"
            phrase="ESCALATE"
            consequences={[
              'The booking moves to disputed and the pandit’s payout is held.',
              'A quality flag is raised against the pandit for somebody to look at.',
              'Both parties are told, and the other side gets a window to answer.',
            ]}
            onCancel={() => setEscalating(false)}
            onConfirm={() =>
              escalate.mutate(
                { code: openCode },
                {
                  onSuccess: (dispute) => {
                    setEscalating(false);
                    toast({ tone: 'success', message: `Opened ${dispute.public_code}` });
                  },
                  onError: (error) => {
                    setEscalating(false);
                    fail(error);
                  },
                },
              )
            }
          />
        )}
      </DetailPage>
    );
  }

  if (intake) {
    return (
      <PhoneIntake
        onDone={(opened) => {
          setIntake(false);
          if (opened) setOpenCode(opened.public_code);
        }}
      />
    );
  }

  return (
    <>
      <FilterBar
        right={
          <ScopeAction scope={scope} need="support" size="sm" onClick={() => setIntake(true)}>
            Phone intake
          </ScopeAction>
        }
        extra={
          <>
            <Select
              label="State"
              value={state}
              options={STATES}
              onChange={(next) => {
                setState(next);
                setPage(1);
              }}
            />
            {/* On the server. A client-side narrowing would filter the page in hand and then
                confidently report no urgent tickets on the rest of the queue. */}
            <Select
              label="Priority"
              value={priority}
              options={PRIORITIES}
              onChange={(next) => {
                setPriority(next);
                setPage(1);
              }}
            />
          </>
        }
      />

      {list.isPending && <LoadingState message="Reading the queue…" />}
      {list.isError && (
        <ErrorState title="Could not read the queue" cause={errorMessage(list.error)} />
      )}
      {list.isSuccess && list.data.items.length === 0 && (
        <EmptyState
          title="Nothing in this queue"
          body="Tickets arrive as devotees and pandits write in, and as agents open them for people who ring."
        />
      )}
      {list.isSuccess &&
        list.data.items.filter(needsSafetyBanner).map((t) => (
          <SafetyBanner key={t.public_code} ticket={t} />
        ))}
      {list.isSuccess && list.data.items.length > 0 && (
        <DataTable
          density="compact"
          onRowClick={(row) => setOpenCode(String(row.id))}
          columns={[
            { key: 'code', label: 'Ticket', mono: true },
            { key: 'subject', label: 'About' },
            { key: 'priority', label: 'Priority' },
            { key: 'state', label: 'State' },
            { key: 'due', label: 'First reply' },
          ]}
          rows={list.data.items.map((t) => ({
            id: t.public_code,
            code: t.public_code,
            subject: t.subject,
            priority: <StatusChip tone={priorityTone(t.priority)} label={t.priority} />,
            state: <StatusChip tone={statusTone(t.status)} label={t.status.replace(/_/g, ' ')} />,
            due: clockLabel(t),
          }))}
          footer={`${list.data.total} ticket${list.data.total === 1 ? '' : 's'}`}
        />
      )}
    </>
  );
}

/** Doc 03 §5.6's banner, always on for a live safety ticket; the chip counts to the next promise. */
function SafetyBanner({
  ticket,
}: {
  ticket: {
    public_code: string;
    first_responded_at?: string | null;
    first_response_due_at: string;
    resolution_due_at: string;
  };
}) {
  return (
    <UrgencyBanner
      until={ticket.first_responded_at ? ticket.resolution_due_at : ticket.first_response_due_at}
      label={ticket.first_responded_at ? 'to resolve' : 'to first reply'}
      urgentWithinMinutes={Number.POSITIVE_INFINITY}
    >
      Safety report {ticket.public_code} — the on-call lead has been paged. Work it before anything
      else.
    </UrgencyBanner>
  );
}
